import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Pagination,
  Switch,
  Tab,
  Tabs,
  Typography,
  styled,
} from '@mui/material'
import DOMPurify from 'dompurify'

import { apiZodios } from '~api/apiZodios'
import { images } from '~assets/images'
import { Avatar, Image } from '~components/common'
import { IS_DEMO, IS_PROD } from '~config'
import { lightTheme } from '~createTheme'
import { RootState } from '~redux/rootReducer'
import {
  ChatDisplaySource,
  IAIInteractionType,
  OrganizationAISettings0,
  QueryAnalysis,
} from '~types'

import AISearchBar from './AISearchBar'
import ChatBubbleProgressIndicator from './ChatBubbleProgressIndicator'
import { QueryAnalysisDebugInfo } from './QueryAnalysisDebugInfo'

const defaultBanner =
  'https://s3-us-east-2.amazonaws.com/polimorphic-sha256/assets/isometricCity.png'

interface SourcesProps {
  sources: ChatDisplaySource[]
  maxSources?: number
  showTitle?: boolean
  testing?: boolean
}

const StyledLink = styled('a')({
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
  },
})

type LoadingState = 'analyzing' | 'retrieving' | 'generating' | 'waiting'

export const Sources: React.FC<SourcesProps> = ({
  sources,
  maxSources,
  showTitle = true,
  testing,
}) => {
  const displaySources = maxSources ? sources.slice(0, maxSources) : sources
  const uniqueSources = Array.from(
    new Map(displaySources.map((source) => [source.url, source])).values(),
  )

  return (
    <Box
      sx={{
        m: 2.5,
        ...(showTitle && { borderTop: '1px solid #888' }),
      }}
    >
      {showTitle && (
        <Typography
          sx={{
            fontFamily: 'avenir-light, sans-serif',
            fontSize: '20px',
            margin: '20px 0 30px',
          }}
        >
          Sources
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {uniqueSources.map((source, index) => (
          <Link
            key={index}
            href={source?.page_gui_url || source?.page_url || undefined}
            target='_blank'
            underline='none'
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: index === 0 ? 0 : 2.5,
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
              cursor: 'pointer',
            }}
            className='double-blue-border-on-focus'
          >
            <Box sx={{ width: 'calc(100% - 45px)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  className='underline-on-hover'
                  sx={{
                    ...lightTheme.typography.link,
                    color: 'var(--blue)',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {source.title}
                </Typography>
                {source.external_link === false && (
                  <Chip
                    label='Hidden'
                    size='small'
                    title='Hidden sources will be used in the AI answer, but will not be shown in the sources list as a link'
                    sx={{ height: '20px' }}
                  />
                )}
                {source.external_source === false && (
                  <Chip
                    label='Private'
                    size='small'
                    title='Private sources will only be used in the AI answer if you are logged in and have access to the organization. External users will not see content/answers that use external sources'
                    sx={{ height: '20px' }}
                  />
                )}
              </Box>
              <Typography
                sx={{
                  mt: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'text.primary',
                }}
              >
                {source.text}
              </Typography>
              {testing && (
                <Typography variant='caption' sx={{ ml: 1, color: 'text.secondary' }}>
                  Score: {source.relevance_score}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                alignItems: 'center',
                borderRadius: '50%',
                display: 'flex',
                height: '25px',
                justifyContent: 'center',
                ml: 1.25,
                mr: 2.5,
                width: '25px',
              }}
            >
              <OpenInNewIcon
                sx={{
                  width: '15px',
                  filter: 'brightness(0)',
                }}
              />
            </Box>
          </Link>
        ))}
      </Box>
    </Box>
  )
}

const formatColor = (color: string | undefined): string => {
  if (!color) return ''
  return color.startsWith('#') ? color : `#${color}`
}

interface QueryViewProps {
  orgSettings: OrganizationAISettings0
  suggestions?: string[]
  isEmbedded?: boolean
  testing?: boolean
  initialQuery?: string
}

interface SearchBoxProps {
  orgSettings: OrganizationAISettings0
  suggestions?: string[]
  testing?: boolean
  initialQuery?: string
}

const postMessageScrollHeightOneLine = (): void =>
  window.parent.postMessage({ scrollHeight: document.body.scrollHeight }, '*')

export const SearchBox: React.FC<SearchBoxProps> = ({
  orgSettings,
  suggestions,
  testing,
  initialQuery,
}) => {
  const emailIdentities = useSelector((state: RootState) => state?.organization.emailIdentities)
  const isAdmin = emailIdentities?.some(
    (identity) => identity.organization_id === orgSettings.organization.id,
  )
  const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)

  const [query, setQuery] = useState<string>('')
  const [responseId, setResponseId] = useState<string | null>(null)
  const [response, setResponse] = useState<string>('')
  const [queryAnalysis, setQueryAnalysis] = useState<QueryAnalysis>()
  const [impression, setImpression] = useState<boolean | null>(null)
  const [responseSources, setResponseSources] = useState<ChatDisplaySource[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isResponseComplete, setIsResponseComplete] = useState<boolean>(false)
  const [userId, setUserId] = useState<string>('')
  const [activeTab, setActiveTab] = useState(0)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sources, setSources] = useState<ChatDisplaySource[]>([])
  const [loadingState, setLoadingState] = useState<LoadingState>('analyzing')

  const [debugVisible, setDebugVisible] = useState<boolean>(true)
  const showDebugInfo = testing || (isSuper && debugVisible) || (!isSuper && !IS_DEMO && !IS_PROD)
  const pageSize = 10

  const poliApplySources = responseSources.filter((source) => source.is_poli_process === true) || []
  const poliApplyUniqueSources =
    Array.from(new Map(poliApplySources.map((source) => [source.url, source])).values()) || []

  const loadTotalPages = async (): Promise<void> => {
    const result = await apiZodios.ai_getPageCount({
      user_id: userId || undefined,
      organization_identity_id: orgSettings.organization.identity,
      organization: orgSettings.organization.id,
      archived: false,
    })
    setTotalPages(Math.ceil(result.data / pageSize))
  }

  const loadSources = async (searchQuery: string): Promise<void> => {
    const result = await apiZodios.ai_listSources({
      user_id: userId || undefined,
      organization_id: orgSettings.organization.id,
      query: searchQuery,
      offset: page * pageSize,
      limit: pageSize,
    })
    setSources(result.sources)
  }

  useEffect(() => {
    if (query === '' || activeTab !== 1) return
    loadSources(query)
  }, [page])

  useEffect(() => {
    postMessageScrollHeightOneLine()
  }, [response, responseSources, isLoading, activeTab, page])

  useEffect(() => {
    if (initialQuery) {
      handleSearch(initialQuery)
    }
  }, [initialQuery])

  const tabsRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLTextAreaElement>(null)
  const searchBoxRef = useRef<HTMLDivElement>(null)
  const responseContentRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef<HTMLDivElement>(null)

  const handleSearch = async (searchQuery: string): Promise<void> => {
    if (!searchQuery || searchQuery.length < 2) {
      setResponse(
        'It seems you have not entered a valid question. Please let me know what I can assist you with regarding ' +
          orgSettings.organization.name +
          '.',
      )
      setActiveTab(0)
      return
    }

    setQuery(searchQuery)
    setIsLoading(true)
    setLoadingState('analyzing')
    setActiveTab(0) // Reset to AI Answer tab on new search
    setPage(0)
    setImpression(null)

    // Focus on loading message
    setTimeout(() => {
      loadingRef.current?.focus()
    }, 100)

    // NOTE: before we transition to streaming updates, display intermediate updates
    const retrievalTimer = setTimeout(() => {
      setLoadingState('retrieving')
    }, 2500)

    const generatingTimer = setTimeout(() => {
      setLoadingState('generating')
    }, 5000)

    const waitingTimer = setTimeout(() => {
      setLoadingState('waiting')
    }, 10000)

    try {
      const result = await apiZodios.ai_generateResponse({
        user_id: userId || undefined,
        organization_id: orgSettings.organization.id || '',
        interaction_type: IAIInteractionType.SEARCH,
        query: searchQuery || '',
      })
      setIsResponseComplete(true)
      clearTimeout(retrievalTimer)
      clearTimeout(generatingTimer)
      clearTimeout(waitingTimer)

      // Focus the AI Answers tab after response loads
      setTimeout(() => {
        const aiAnswerTab = tabsRef.current?.querySelector('[role="tab"]') as HTMLElement
        aiAnswerTab?.focus()
      }, 100)

      setTimeout(() => {
        setResponse(result.response_text)
        setResponseId(result.response_id)
        setQueryAnalysis(result.query_analysis)
        setUserId(result.user_id || '')
        setResponseSources(result.sources)

        // Load total pages and sources only after a successful search
        if (result.sources && result.sources.length > 0) {
          loadTotalPages()
          loadSources(searchQuery)
        }
        setIsResponseComplete(false)
        setIsLoading(false)
      }, 500)
    } catch (error) {
      console.error('Error generating AI response:', error)
      setResponse('An error occurred while generating the response.')
      clearTimeout(retrievalTimer)
      clearTimeout(generatingTimer)
      clearTimeout(waitingTimer)
    }
  }

  const handleClear = (): void => {
    setQuery('')
    setResponse('')
    setImpression(null)
    setPage(0)
    setResponseSources([])
    setSources([])
    setActiveTab(0)
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 0)
  }

  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.getElementsByTagName('a')
      for (let i = 0; i < links.length; i++) {
        const link = links[i]
        link.addEventListener('click', (e) => {
          e.preventDefault()
          const href = link.getAttribute('href')
          if (href) {
            window.open(href, '_blank', 'noopener,noreferrer')
          }
        })
      }
    }
  }, [response])

  const processHtml = (html: string): string => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Make all links tabbable
    const links = doc.getElementsByTagName('a')
    for (let i = 0; i < links.length; i++) {
      const link = links[i]
      link.setAttribute('target', '_blank')
      link.setAttribute('rel', 'noopener noreferrer')
      link.setAttribute('tabindex', '0')
    }

    return DOMPurify.sanitize(doc.body.innerHTML)
  }

  return (
    <Box ref={searchBoxRef} sx={{ outline: 'none' }} aria-label='Polimorphic Search'>
      <Box
        sx={{
          backgroundColor: formatColor(orgSettings.organization.bg_color),
          padding: '20px',
          color: formatColor(orgSettings.organization.font_color),
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant='h6' sx={{ fontSize: '22px' }}>
          AI-Powered Search for <strong>{orgSettings.organization.name}</strong>
        </Typography>
      </Box>
      <AISearchBar
        onSearch={handleSearch}
        suggestions={suggestions}
        hasResults={response != null && response.trim() !== ''}
        query={query}
        onQueryChange={setQuery}
        inputRef={searchInputRef}
      />

      {(response || responseSources.length > 0) && (
        <Box sx={{ borderTop: '1px solid #F0F0F0' }}>
          {showDebugInfo && queryAnalysis && (
            <QueryAnalysisDebugInfo queryAnalysis={queryAnalysis} />
          )}
          {isAdmin && (
            <Alert severity='info' sx={{ mx: 2.5, mt: 2.5 }}>
              You are logged into {orgSettings.organization.name} so you will see results and
              sources external users do not see.
            </Alert>
          )}
          <Box sx={{ display: 'flex', p: '10px 20px 0' }} tabIndex={-1}>
            <Tabs
              ref={tabsRef}
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label='Search results tabs'
              TabScrollButtonProps={{
                tabIndex: -1, // Prevent scroll buttons from being tabbable
              }}
              sx={{
                '& .MuiTabs-scroller': {
                  '& [role="tab"]': {
                    '&:focus': {
                      borderBottom: '3px solid rgb(8, 148, 207)',
                    },
                  },
                },
              }}
            >
              <Tab
                label='AI Answer'
                sx={{
                  bgcolor: activeTab === 0 ? 'rgb(230, 244, 250)' : 'transparent',
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
                aria-label='AI generated answer'
                role='tab'
                tabIndex={0}
              />
              {responseSources.length > 0 && (
                <Tab
                  label='All Results'
                  sx={{
                    bgcolor: activeTab === 1 ? 'rgb(230, 244, 250)' : 'transparent',
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 'bold',
                  }}
                  aria-label='All search results'
                  role='tab'
                  tabIndex={0}
                />
              )}
            </Tabs>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              onClick={handleClear}
              sx={{
                color: 'rgb(8, 148, 207)',
                fontWeight: 'bold',
                textTransform: 'none',
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>
      )}

      {isLoading ? (
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='center'
          alignItems='center'
          minHeight='150px'
          role='status'
          aria-live='assertive'
          tabIndex={0}
          ref={loadingRef}
        >
          <ChatBubbleProgressIndicator
            loadingState={loadingState}
            onComplete={isResponseComplete}
            showBubble={false}
          />
        </Box>
      ) : (
        response && (
          <>
            {activeTab === 0 ? (
              <>
                <Box
                  ref={responseContentRef}
                  sx={{
                    p: 2.5,
                  }}
                  onLoad={postMessageScrollHeightOneLine}
                >
                  {poliApplyUniqueSources.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {poliApplyUniqueSources.map((source, index) => (
                        <StyledLink
                          key={index}
                          href={source?.page_gui_url || source?.page_url || undefined}
                          target='_blank'
                          rel='noopener noreferrer'
                          tabIndex={0}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            border: `1px solid #${orgSettings.organization.bg_color}`,
                            backgroundColor: `#${orgSettings.organization.bg_color}22`,
                            borderRadius: '5px',
                            marginBottom: '20px',
                            padding: '15px',
                          }}
                        >
                          <Box sx={{ width: '24px', marginRight: '8px' }}>
                            <Image src={images.logo} size='24px' alt='Polimorphic logo' />
                          </Box>
                          <Typography variant='body2'>
                            Click to Apply Online: {source.title}
                          </Typography>
                        </StyledLink>
                      ))}
                    </Box>
                  )}
                  <Box
                    sx={{
                      '& a': {
                        ...lightTheme.typography.link,
                        color: 'var(--blue)',
                        textDecoration: 'none',
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                        '&:focus-visible': {
                          border: '2px solid #ffffff',
                          outline: '2px solid #0894cf',
                          outlineOffset: '2px',
                          boxShadow: '0 0 0 2px #98e0ff, 0 0 0 4px #98e0ff',
                        },
                      },
                      '& p': {
                        display: 'block',
                        margin: '1.5 0',
                        lineHeight: 1.6,
                      },
                      '& li': {
                        marginBottom: 1,
                        lineHeight: 1.6,
                      },
                      '& li p': {
                        display: 'inline',
                        margin: 0,
                      },
                      '& ul, & ol': {
                        marginTop: 1.5,
                        marginBottom: 1.5,
                        paddingLeft: 3,
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        margin: '3 0 1.5',
                        lineHeight: 1.3,
                      },
                      '& h1': { fontSize: 24 },
                      '& h2': { fontSize: 20 },
                      '& h3': { fontSize: 18 },
                      '& blockquote': {
                        borderLeft: '4px solid #e0e0e0',
                        margin: '2 0',
                        padding: '1 0 1 2',
                        fontStyle: 'italic',
                        color: 'text.secondary',
                      },
                      '& code': {
                        backgroundColor: '#f5f5f5',
                        padding: '0.5 1',
                        borderRadius: '3px',
                        fontSize: 14,
                        fontFamily: 'monospace',
                      },
                      '& pre': {
                        backgroundColor: '#f5f5f5',
                        padding: 2,
                        borderRadius: '4px',
                        overflow: 'auto',
                        margin: '2 0',
                      },
                      '& pre code': {
                        padding: 0,
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <div
                      ref={contentRef}
                      className='prose max-w-none'
                      dangerouslySetInnerHTML={{ __html: processHtml(response) }}
                      role='article'
                      aria-label='AI Response'
                    />
                  </Box>
                  {responseId && (
                    <Box
                      sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}
                      role='group'
                      aria-label='Feedback buttons'
                    >
                      <Typography sx={{ color: 'text.secondary' }} tabIndex={0}>
                        Was this answer helpful?
                      </Typography>
                      <IconButton
                        onClick={() => setImpression(impression === true ? null : true)}
                        color={impression === true ? 'success' : 'default'}
                        aria-label='Mark as helpful'
                        tabIndex={0}
                      >
                        <ThumbUpOffAltIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => setImpression(impression === false ? null : false)}
                        color={impression === false ? 'warning' : 'default'}
                        aria-label='Mark as not helpful'
                        tabIndex={0}
                      >
                        <ThumbDownOffAltIcon />
                      </IconButton>
                    </Box>
                  )}
                  {impression === true && (
                    <Alert severity='success' sx={{ mt: 1 }}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            orgSettings.upvote_custom_text || 'Glad we could help!',
                          ),
                        }}
                      />
                    </Alert>
                  )}
                  {impression === false && (
                    <Alert severity='info' sx={{ mt: 1 }}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            orgSettings.downvote_custom_text ||
                              'Sorry this answer was not helpful. Try wording your question differently. The more details the better. Or click through the sources listed.',
                          ),
                        }}
                      />
                    </Alert>
                  )}
                </Box>
                {responseSources.length > 0 && (
                  <Sources sources={responseSources} maxSources={5} testing={testing} />
                )}
              </>
            ) : (
              <>
                <Sources sources={sources} showTitle={false} testing={testing} />
                {totalPages > 1 && (
                  <Box
                    mt={2}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Pagination
                      aria-label='Search results pagination'
                      count={totalPages}
                      page={page + 1}
                      onChange={(_, value) => setPage(value - 1)}
                    />
                  </Box>
                )}
              </>
            )}
            {orgSettings.organization.search_url && (
              <NotFoundHelp websiteSearchUrl={orgSettings.organization.search_url || ''} />
            )}
          </>
        )
      )}
      <SearchDisclaimer orgSettings={orgSettings} />
      <SearchFooter
        isSuper={isSuper || false}
        debugVisible={debugVisible}
        onToggleDebug={setDebugVisible}
        orgSettings={orgSettings}
      />
    </Box>
  )
}

interface NotFoundHelpProps {
  websiteSearchUrl: string
}

const NotFoundHelp: React.FC<NotFoundHelpProps> = ({ websiteSearchUrl }) => {
  return (
    <Box
      sx={{
        m: 2.5,
        borderTop: '1px solid #888',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'avenir-light, sans-serif',
          fontSize: '20px',
          margin: '20px 0 30px',
        }}
      >
        Not finding what you are looking for?
      </Typography>
      <Typography
        sx={{
          fontFamily: 'avenir-book, sans-serif',
          fontSize: '16px',
          letterSpacing: '0.25px',
          lineHeight: '24px',
          mt: 1.875,
        }}
      >
        Try rephrasing your question, or being more specific. If your initial question was too broad
        or vague, try narrowing it down and providing more specific details. If you need critical or
        complex information that AI can't provide, consider contacting your local government
        directly.
      </Typography>
      <Box sx={{ display: 'flex', mt: 1.875 }}>
        <Link
          href={websiteSearchUrl}
          target='_blank'
          underline='none'
          sx={{
            width: 'calc(100% - 45px)',
            '&:hover': {
              bgcolor: '#f5f5f5',
            },
          }}
          className='double-blue-border-on-focus'
        >
          <Typography
            sx={{
              fontFamily: 'avenir-book, sans-serif',
              fontSize: '16px',
              letterSpacing: '0.25px',
              lineHeight: '24px',
            }}
          >
            Want to try searching without AI?{' '}
            <span
              style={{
                color: 'rgb(8, 148, 207)',
                fontFamily: 'avenir-medium, sans-serif',
                textDecoration: 'none',
              }}
            >
              Go to Website Search
            </span>
          </Typography>
        </Link>
      </Box>
    </Box>
  )
}

const SearchDisclaimer: React.FC<{ orgSettings?: OrganizationAISettings0 }> = ({ orgSettings }) => {
  return (
    <Typography
      sx={{
        textAlign: 'center',
        color: lightTheme.palette.text.primary,
        fontSize: '14px',
        p: 1,
      }}
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(
          orgSettings?.disclaimer_text ||
            'DISCLAIMER: AI may produce inaccurate information about people, places, or facts.',
        ),
      }}
    />
  )
}

const SearchFooter: React.FC<{
  isSuper?: boolean
  debugVisible?: boolean
  onToggleDebug?: (visible: boolean) => void
  orgSettings?: OrganizationAISettings0
}> = ({
  isSuper = false,
  debugVisible = false,
  onToggleDebug = (): void => {},
  orgSettings,
}): React.ReactElement => {
  // HACK - Don't link back to Polimorphic.com for Morris County Surrogate
  const organizationId = orgSettings?.organization.id
  const morrisCountySurrogateOrganizationIdProd = 'ffa09fe1-aaea-4984-9e13-9fa483857e2b'
  const includePolimorphicLink = organizationId !== morrisCountySurrogateOrganizationIdProd
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2.5,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {isSuper && (
          <Box mr={2}>
            <FormControlLabel
              control={
                <Switch
                  size='small'
                  checked={debugVisible}
                  onChange={(e) => onToggleDebug(e.target.checked)}
                  color='primary'
                />
              }
              label={
                <Typography sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                  Debug Info
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>
        )}
        <Image src={images.logo} size='15px' alt='Polimorphic logo' tabIndex={0} />
        {includePolimorphicLink ? (
          <Link
            href={'https://polimorphic.com'}
            target='_blank'
            tabIndex={0}
            underline='none'
            sx={{ color: 'black', '&:focus': { textDecoration: 'underline' } }}
          >
            <Typography
              component='span'
              sx={{
                cursor: 'pointer',
                fontFamily: 'monospace',
                ml: '5px',
                fontSize: '14px',
              }}
            >
              Powered by Polimorphic
            </Typography>
          </Link>
        ) : (
          <Typography
            component='span'
            sx={{
              fontFamily: 'monospace',
              ml: '5px',
              fontSize: '14px',
            }}
          >
            Powered by Polimorphic
          </Typography>
        )}
      </Box>
    </Box>
  )
}

const QueryView: React.FC<QueryViewProps> = ({
  orgSettings,
  suggestions,
  isEmbedded,
  testing,
  initialQuery,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {!isEmbedded && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: formatColor(orgSettings.organization.bg_color),
              padding: '20px',
              color: formatColor(orgSettings.organization.font_color),
              gap: 2,
            }}
          >
            {orgSettings.organization.image && (
              <Box
                role='img'
                tabIndex={0}
                aria-label={`${orgSettings.organization.name} logo`}
                sx={{
                  display: 'inline-block',
                  '&:focus': {
                    outline: '2px solid rgb(8, 148, 207)',
                    outlineOffset: '2px',
                    borderRadius: '50%',
                  },
                }}
              >
                <Avatar src={orgSettings.organization.image} size='60px' />
              </Box>
            )}
            <Typography sx={{ fontSize: '22px' }} tabIndex={0}>
              {orgSettings.organization.name}
            </Typography>
          </Box>
          <img
            src={orgSettings.organization.banner || defaultBanner}
            alt='Organization banner'
            style={{
              width: '100%',
              height: '200px',
              objectFit: 'cover',
              borderBottom: '1px solid #000',
            }}
          />
          <Divider />
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          p: isEmbedded ? '0' : '20px',
          width: '100%',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            ...(isEmbedded
              ? {}
              : {
                  maxWidth: 800,
                  mx: 'auto',
                }),
            alignItems: 'center',
          }}
        >
          <SearchBox
            orgSettings={orgSettings}
            suggestions={suggestions}
            testing={testing}
            initialQuery={initialQuery}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default QueryView
